import React, { useEffect, useMemo, useState } from 'react';

import { Button, Divider, RadioGroup, Select, Switch, TextInput, withPage } from '@sms/plasma-ui';

import {
  CardModeEnum,
  GroupByEnum,
  ITaskFilter,
  Task,
  taskApi,
  TaskCardItem,
  TaskList,
  ViewModeEnum,
  TaskFilter,
  FilterState,
  TaskView,
} from '@sms/journal-task-journal-task-ui';

import * as styled from './TestTaskEmbeddedPage.styles';

enum Type {
  Iframe = 'Iframe',
  List = 'List',
  View = 'View',
  Item = 'Item',
  Filter = 'Filter',
}

function TestTaskEmbeddedPage() {
  const [type, setType] = useState<Type>(Type.Iframe);
  const [inputUrl, setInputUrl] = useState<string>();
  const [url, setUrl] = useState(inputUrl);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [filter, setFilter] = useState<FilterState>({ periodStart: new Date(2026, 3, 1), periodEnd: new Date(2027, 0, 1) });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showReactionsCount, setShowReactionsCount] = useState(true);

  const [selectable, setSelectable] = useState(false);
  const [selected, setSelected] = useState(false);
  const [singleView, setSingleView] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showDownloadIconInActions, setShowDownloadIconInActions] = useState(false);
  const [showDownloadButton, setShowDownloadButton] = useState(false);
  const [minimalistMode, setMinimalistMode] = useState(false);
  const [checked, setChecked] = useState(false);

  const [viewMode, setViewMode] = useState<ViewModeEnum>(ViewModeEnum.list);
  const [groupBy, setGroupBy] = useState<GroupByEnum>(GroupByEnum.category);
  const [cardMode, setCardMode] = useState<CardModeEnum>(CardModeEnum.detailed);
  const [enableSelection, setEnableSelection] = useState(false);
  const [tasksSelectable, setTasksSelectable] = useState(false);
  const [hideEditButton, setHideEditButton] = useState(false);
  const [hideReopenButton, setHideReopenButton] = useState(false);
  const [hideSaveButton, setHideSaveButton] = useState(false);
  const [hideCompleteButton, setHideCompleteButton] = useState(false);
  const [hideDelegateButton, setHideDelegateButton] = useState(false);
  const [hideWontDoButton, setHideWontDoButton] = useState(false);
  const [hideClaimButton, setHideClaimButton] = useState(false);
  const [hideUnclaimButton, setHideUnclaimButton] = useState(false);
  const [hideCommentsTab, setHideCommentsTab] = useState(false);
  const [hideAttachmentsTab, setHideAttachmentsTab] = useState(false);
  const [hideLinksTab, setHideLinksTab] = useState(false);
  const [hideHistoryTab, setHideHistoryTab] = useState(false);

  const loadTasks = async (page: number) => {
    setLoading(true);
    const taskFilter: ITaskFilter = {
      user: 'bd5ebc96-aec7-4cf7-b8bb-1e3e5f495a4e',
      startDate: filter.periodStart ?? new Date(2025, 3, 1),
      finishDate: filter.periodEnd ?? new Date(2027, 0, 1),
      completed: false,
      page: page,
      pageSize: 30,
    };
    const result = await taskApi.getTasks(taskFilter);

    setTasks(prev => [...prev, ...result.Data]);
    setHasMore(result.Data.length === 30);
    setLoading(false);
  }

  const loadNextPage = () => {
    if (hasMore && !loading)
      setPage(prev => prev + 1);
  };

  const taskWithReactions = useMemo(() => tasks.find(e => e.ReactionCount), [tasks]);

  useEffect(() => {
    loadTasks(page);
  }, [page]);

  const executionOptions = (
    <>
      <Switch name='hideCommentsTab' label="Hide comments tab" checked={hideCommentsTab} onChange={setHideCommentsTab} />
      <Switch name='hideAttachmentsTab' label="Hide attachments tab" checked={hideAttachmentsTab} onChange={setHideAttachmentsTab} />
      <Switch name='hideLinksTab' label="Hide links tab" checked={hideLinksTab} onChange={setHideLinksTab} />
      <Switch name='hideHistoryTab' label="Hide history tab" checked={hideHistoryTab} onChange={setHideHistoryTab} />
      <Switch name='hideEditButton' label="Hide edit button" checked={hideEditButton} onChange={setHideEditButton} />
      <Switch name='hideReopenButton' label="Hide reopen button" checked={hideReopenButton} onChange={setHideReopenButton} />
      <Switch name='hideSaveButton' label="Hide save button" checked={hideSaveButton} onChange={setHideSaveButton} />
      <Switch name='hideCompleteButton' label="Hide complete button" checked={hideCompleteButton} onChange={setHideCompleteButton} />
      <Switch name='hideDelegateButton' label="Hide delegate button" checked={hideDelegateButton} onChange={setHideDelegateButton} />
      <Switch name='hideWontDoButton' label="Hide won't do button" checked={hideWontDoButton} onChange={setHideWontDoButton} />
      <Switch name='hideClaimButton' label="Hide claim button" checked={hideClaimButton} onChange={setHideClaimButton} />
      <Switch name='hideUnclaimButton' label="Hide unclaim button" checked={hideUnclaimButton} onChange={setHideUnclaimButton} />
    </>
  );

  return (
    <styled.TestTaskEmbeddedPage>
      <styled.MenuContainer>
        <RadioGroup
          name='radio'
          label='Component'
          dataSource={[
            'Iframe',
            'List',
            'View',
            'Item',
            'Filter',
          ].map(item => ({ label: item, value: item }))}
          value={type}
          onChange={e => setType(e.target.value)}
        />

        {
          type === Type.Iframe &&
          <div className="url-input">
            <TextInput name="url" label='URL' onChange={(v) => setInputUrl(v.target.value)} value={inputUrl} />
            <Button type="primary" title="Load" onClick={() => setUrl(inputUrl)} />
          </div>
        }

        {type === Type.View &&
          <>
            {executionOptions}
          </>
        }

        {(type === Type.List) &&
          <>
            <Switch name='showReactionsCount' label="Show reactions count" checked={showReactionsCount} onChange={setShowReactionsCount} />
            <Switch name='enableSelection' label="Enable selection" checked={enableSelection} onChange={setEnableSelection} />
            <Select name="viewMode" label="View mode" onChange={setViewMode} value={viewMode} dataSource={[
              { label: 'List', value: ViewModeEnum.list },
              { label: 'Inbox', value: ViewModeEnum.inbox },
            ]} />
            <Select name="groupBy" label="Group by" onChange={setGroupBy} value={groupBy} dataSource={[
              { label: 'Category', value: GroupByEnum.category },
              { label: 'Due date', value: GroupByEnum.dueDate },
              { label: 'Criticality', value: GroupByEnum.criticality },
              { label: 'Status', value: GroupByEnum.status },
            ]} />
            <Select name="cardMode" label="Card mode" onChange={setCardMode} value={cardMode} dataSource={[
              { label: 'Detailed', value: CardModeEnum.detailed },
              { label: 'Simple', value: CardModeEnum.simple },
            ]} />

            {
              enableSelection &&
              <Switch name='tasksSelectable' label="Tasks selectable" checked={tasksSelectable} onChange={setTasksSelectable} />
            }

            {
              viewMode === ViewModeEnum.inbox && executionOptions
            }
          </>
        }

        {(type === Type.Item) &&
          <>
            <Switch name='showReactionsCount' label="Show reactions count" checked={showReactionsCount} onChange={setShowReactionsCount} />
            <Switch name='selectable' label="Selectable" checked={selectable} onChange={setSelectable} />
            <Switch name='selected' label="Selected" checked={selected} onChange={setSelected} />
            <Switch name='singleView' label="Single view" checked={singleView} onChange={setSingleView} />
            <Switch name='showDetails' label="Show details" checked={showDetails} onChange={setShowDetails} />
            <Switch name='showDownloadIconInActions' label="Show download icon in actions" checked={showDownloadIconInActions} onChange={setShowDownloadIconInActions} />
            <Switch name='showDownloadButton' label="Show download button" checked={showDownloadButton} onChange={setShowDownloadButton} />
            <Switch name='minimalistMode' label="Minimalist mode" checked={minimalistMode} onChange={setMinimalistMode} />
            <Switch name='checked' label="Checked" checked={checked} onChange={setChecked} />
          </>
        }
      </styled.MenuContainer>

      <Divider style={{ margin: 0 }} />

      <styled.ContentContainer>
        {
          type === Type.Iframe &&
          <styled.IframeContainer>
            <iframe src={url} />
          </styled.IframeContainer>
        }

        {type === Type.Filter &&
          <TaskFilter
            filter={filter}
            onChange={filter => { setFilter(filter); console.log({ filter }); }}
          >
            <p>Sample</p>
            <p>
              Lorem ipsum dolor sit amet, consectetur adipiscing elit. Proin enim felis, molestie eu efficitur et, sodales id risus. Curabitur sed eros id nibh hendrerit semper. Suspendisse fermentum suscipit quam, non mollis enim. Nulla facilisi. Sed dui nisl, pellentesque eget nisl et, maximus posuere metus. Duis nec justo sapien. Aliquam ultrices mauris in consequat posuere. Nullam auctor, diam non suscipit varius, ligula diam elementum diam, sed lobortis felis arcu vel elit. Sed posuere dignissim faucibus. Duis tempus ante ex, a elementum dui scelerisque ac. Nulla at condimentum neque, eget euismod quam. Aliquam volutpat eros ornare quam molestie, id convallis velit consequat.
            </p>
            <p>
              Fusce a molestie diam, quis tincidunt felis. Sed in hendrerit lectus. Sed maximus quis ligula et ultricies. Vestibulum ante ipsum primis in faucibus orci luctus et ultrices posuere cubilia curae; Nulla vitae vulputate nisl. Etiam eu euismod nibh, eu tincidunt dolor. Sed orci diam, tincidunt eget metus eu, convallis pharetra ipsum. Nam finibus mauris quis dolor pulvinar, vel dignissim sapien feugiat. Etiam sit amet rhoncus massa. Nulla vel laoreet ex. Aliquam quis molestie augue, sit amet varius nisi.
            </p>
            <p>
              Duis vel leo porta, suscipit nisi eu, malesuada erat. Donec sit amet diam ut purus aliquet cursus. Praesent placerat ut arcu at vehicula. Vestibulum et lectus sed felis efficitur finibus. Aliquam mattis sed neque sit amet imperdiet. Duis ac ex et sem pellentesque gravida. Proin consequat dolor justo, a venenatis mi sodales vel. Donec volutpat eu risus id fringilla. Duis mollis, turpis a finibus facilisis, mauris dui laoreet urna, ut faucibus ipsum leo et ipsum. Mauris ullamcorper ex eget lorem sodales, ac rhoncus mi sodales. Morbi tincidunt nunc quis est rutrum vestibulum. Suspendisse porta neque nec turpis aliquam commodo. Vivamus libero turpis, convallis ut est vitae, posuere efficitur ipsum.
            </p>
            <p>
              Ut a tincidunt tortor. Praesent non enim mi. Etiam gravida pharetra odio. Phasellus nec gravida libero. Cras pellentesque aliquam rutrum. Phasellus ornare leo id arcu fermentum, a volutpat lacus interdum. Suspendisse egestas lectus porttitor libero bibendum, vel interdum risus porta. Vivamus pretium varius lorem, nec aliquam nunc facilisis sit amet. Aenean ut dolor ut velit dictum dignissim vitae suscipit leo. Suspendisse metus nisl, varius vitae nibh at, pretium egestas dui. Suspendisse consectetur fermentum faucibus. Pellentesque ullamcorper metus vitae leo congue, eu mattis odio euismod. Cras gravida lorem eros, ut pretium magna accumsan at. Ut porttitor commodo odio, hendrerit rutrum nisl rutrum nec. Morbi tempor enim ornare lobortis mattis.
            </p>
          </TaskFilter>
        }

        {type === Type.View &&
          tasks.length > 0 &&
          <TaskView
            task={taskWithReactions || tasks[0]}
            hideAttachmentsTab={hideAttachmentsTab}
            hideCommentsTab={hideCommentsTab}
            hideLinksTab={hideLinksTab}
            hideHistoryTab={hideHistoryTab}
            hideEditButton={hideEditButton}
            hideReopenButton={hideReopenButton}
            hideSaveButton={hideSaveButton}
            hideCompleteButton={hideCompleteButton}
            hideDelegateButton={hideDelegateButton}
            hideWontDoButton={hideWontDoButton}
            hideClaimButton={hideClaimButton}
            hideUnclaimButton={hideUnclaimButton}
            onAfterExecuteAction={() => console.log('after execute action')}
            allowsEditing
          />
        }

        {type === Type.Item &&
          tasks.length > 0 &&
          <TaskCardItem
            task={tasks[0]}
            onClick={() => console.log('click task')}
            showReactionsCount={showReactionsCount}
            showDetails={showDetails}
            showDownloadButton={showDownloadButton}
            showDownloadIconInActions={showDownloadIconInActions}
            checked={checked}
            minimalistMode={minimalistMode}
            selectable={selectable}
            selected={selected}
            singleView={singleView}
            onDownload={task => console.log('download task', task)}
            onLongPress={() => console.log('long press task')}
            onToggleCheck={() => console.log('toggle check task')}
            extra={
              <>
                <p>Sample</p>
                <p>
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit. Proin enim felis, molestie eu efficitur et, sodales id risus.
                </p>
              </>
            }
          />
        }

        {type === Type.List &&
          <TaskList
            dataSource={tasks}
            loading={loading}
            onLoadNextPage={loadNextPage}
            height="calc(100vh - 49px)"
            showReactionsCount={showReactionsCount}
            cardMode={cardMode}
            groupBy={groupBy}
            viewMode={viewMode}
            onOpenGroup={console.log}
            showDownloadButton={showDownloadButton}
            onClickTask={task => console.log('click task', task)}
            selectionSettings={{
              enable: enableSelection,
              selectable: tasksSelectable,
              onOpenSelection: () => setTasksSelectable(true),
              onChangeSelectedTasks: console.log,
            }}
            inboxSettings={{
              hideEditButton,
              hideReopenButton,
              hideSaveButton,
              hideCompleteButton,
              hideDelegateButton,
              hideWontDoButton,
              hideClaimButton,
              hideUnclaimButton,
              hideCommentsTab,
              hideAttachmentsTab,
              hideLinksTab,
              hideHistoryTab,
              onAfterExecuteAction: () => console.log('after execute action'),
            }}
          />
        }
      </styled.ContentContainer>
    </styled.TestTaskEmbeddedPage>
  );
}

export default withPage(TestTaskEmbeddedPage);
